import { ContentLayout } from '@/bundle/Layouts/ContentLayout/ContentLayout';
import PlusIcon from '@/images/PlusIcon.svg?react';
import { Button } from '@/components/Button/Button';
import { PageBody } from '@/components/Page/PageBody/PageBody';
import { NoResults } from '@/components/NoResults/NoResults';
import NoTransactionsIcon from '../../../images/NoTransactionsIcon.svg?react';
import { getListData, showSnackbarResponseError } from '@/helpers/helpers';
import { useGetSearchParams } from '@/hooks/useGetSearchParams';
import { ApiKeyList } from '@/bundle/_MasterAdmin/_DevelopersPage/ui/ApiKeyList';
import { PageFooter } from '@/components/Page/PageFooter/PageFooter';
import { Paginator } from '@/components/Paginator/Paginator';
import { Drawer } from '@/components/Drawer/Drawer';
import { useState } from 'react';
import { SuccessApiKeyCreatedModal } from '@/bundle/_MasterAdmin/_DevelopersPage/CreateApiKeyForm/ui/SuccessApiKeyCreatedModal/SuccessApiKeyCreatedModal';
import { ConfirmModal } from '@/components/ConfirmModal/ConfirmModal';
import { CreateApiKeyForm } from '@/bundle/_MasterAdmin/_DevelopersPage/CreateApiKeyForm/CreateApiKeyForm';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { deleteApiKey, getApiKeys } from '@/bundle/_MasterAdmin/_DevelopersPage/api/api';
import { ApiKeyType } from '@/types/sharedTypes';
import { CreatedApiKeyType } from '@/api/v1/api-key/createApiKeyApi';
import { useReplaceSearchParams } from '@/hooks/useReplaceSearchParams';

export const DevelopersPage = () => {
  const queryClient = useQueryClient();
  const searchParams = useGetSearchParams();
  const { replaceSearchParams } = useReplaceSearchParams();
  const [isEdit, setIsEdit] = useState(false);
  const [isOpenCreateApiKeyDrawer, setIsOpenCreateApiKeyDrawer] = useState(false);
  const [selectedApiKey, setSelectedApiKey] = useState<ApiKeyType | null>(null);
  const [isOpenSuccessModal, setIsOpenSuccessModal] = useState(false);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [createdApiKeyData, setCreateApiKeyData] = useState<CreatedApiKeyType | null>(null);

  const { data, isPending } = useQuery({
    queryKey: ['get_api_keys', searchParams],
    queryFn: () => getApiKeys(searchParams),
  });

  const { mutate, isPending: isDeleteApiKeyPending } = useMutation({
    mutationKey: ['delete_api_key'],
    mutationFn: () => deleteApiKey(selectedApiKey?.id),
    onSuccess(response) {
      if (response?.error) {
        return showSnackbarResponseError(response?.error);
      }

      const currentPage = searchParams.page;
      const currentItems = data?.body?.results || [];

      if (currentPage > 0 && currentItems?.length === 1) {
        const page = currentPage > 1 ? currentPage - 1 : null;

        replaceSearchParams({ page });
      }

      queryClient.invalidateQueries({ queryKey: ['get_api_keys', searchParams] });
      onCloseDeleteModal();
    },
  });

  const onEdit = (apiKey: ApiKeyType) => {
    setSelectedApiKey(apiKey);
    setIsOpenCreateApiKeyDrawer(true);
    setIsEdit(true);
  };

  const onDelete = (apiKey: ApiKeyType) => {
    setSelectedApiKey(apiKey);
    setIsOpenDeleteModal(true);
  };

  const onSuccess = (createdApiKey: CreatedApiKeyType) => {
    setCreateApiKeyData(createdApiKey);
    setIsOpenCreateApiKeyDrawer(false);
    setIsOpenSuccessModal(true);
    setSelectedApiKey(null);
  };

  const onClose = () => {
    setSelectedApiKey(null);
    setIsOpenCreateApiKeyDrawer(false);
    setIsEdit(false);
  };

  const onCloseDeleteModal = () => {
    setIsOpenDeleteModal(false);
    setSelectedApiKey(null);
  };

  const { hasData, hasPagination, list, totalCount } = getListData(data?.body, searchParams);
  const noResults = !hasData && !isPending;
  const drawerHeader = isEdit ? 'Edit API Key' : 'Create API Key';
  const drawerSubHeader = isEdit ? 'Please edit API Key info.' : 'Please provide info for a new API key.';

  return (
    <>
      <ContentLayout
        title='API Keys'
        subtitle='Manage API keys of your organization below.'
        breadcrumbs='API Keys'
        headerAction={
          <Button
            width={182}
            mobileStretch
            icon={<PlusIcon />}
            iconPosition='left'
            onClick={() => {
              setIsOpenCreateApiKeyDrawer(true);
            }}
          >
            Create API Key
          </Button>
        }
      >
        <PageBody>
          {hasData && <ApiKeyList list={list} onEdit={onEdit} onDelete={onDelete} />}
          {noResults && <NoResults icon={<NoTransactionsIcon />} message='Please create your API key.' />}
        </PageBody>
        {hasPagination && (
          <PageFooter>
            <Paginator page={searchParams.page} limit={searchParams.limit} total={totalCount} />
          </PageFooter>
        )}
      </ContentLayout>

      <Drawer isOpen={isOpenCreateApiKeyDrawer} onClose={onClose} header={drawerHeader} subHeader={drawerSubHeader}>
        <CreateApiKeyForm onClose={onClose} apiKey={selectedApiKey} onSuccess={onSuccess} isEdit={isEdit} />
      </Drawer>

      <SuccessApiKeyCreatedModal
        isOpen={isOpenSuccessModal}
        onClose={() => setIsOpenSuccessModal(false)}
        apiKeyData={createdApiKeyData}
      />
      <ConfirmModal
        isOpen={isOpenDeleteModal}
        header='Delete API Key'
        body='Once the API key is deleted, any applications or services currently using it will immediately lose access to WireVault. This action cannot be undone.'
        onClose={onCloseDeleteModal}
        onConfirm={mutate}
        isLoading={isDeleteApiKeyPending}
      />
    </>
  );
};
